import React, { useState, useCallback, useEffect, memo } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { X, RotateCw, RotateCcw, ZoomIn, ZoomOut, Check, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getCroppedImg } from '../../utils/cropImage';
import i18n from '../../i18n';

const OUTPUT_SIZE = 512;

const AvatarCropModal = memo(({ isOpen, onClose, imageFile, onSave, title = i18n.t('shared:avatarCropModal.defaultTitle') }) => {
  const { t } = useTranslation('shared');
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!imageFile || !isOpen) return undefined;

    const url = URL.createObjectURL(imageFile);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);

    return () => {
      URL.revokeObjectURL(url);
      setImageSrc(null);
    };
  }, [imageFile, isOpen]);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleRotate = (dir) => {
    setRotation((prev) => (prev + dir * 90 + 360) % 360);
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels || isProcessing) return;
    setIsProcessing(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation, OUTPUT_SIZE, 0.88);
      const file = new File([blob], 'avatar.webp', { type: 'image/webp' });
      await onSave(file);
      onClose();
    } catch {
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'error', title: t('avatarCropModal.saveErrorTitle'), message: t('avatarCropModal.saveErrorMessage') },
        })
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/75 backdrop-blur-sm">
      <div
        className="bg-[#0d1424] rounded-2xl shadow-2xl border border-white/[0.08] w-full max-w-sm mx-4 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <span className="text-white font-semibold text-sm">{title}</span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/10 text-[#4d6a8a] hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col items-center py-5 px-4 gap-4">
          <div
            className="relative rounded-xl overflow-hidden bg-[#080e1a]"
            style={{ width: 320, height: 320 }}
          >
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                zoomWithScroll
                restrictPosition
                objectFit="contain"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            )}
          </div>

          <div className="flex items-center gap-2 w-full">
            <button
              onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
              className="p-2 rounded-lg bg-[#161f31] hover:bg-[#1e2d47] text-[#8aaccc] transition-colors"
            >
              <ZoomOut size={15} />
            </button>
            <input
              type="range"
              min={100}
              max={400}
              value={Math.round(zoom * 100)}
              onChange={(e) => setZoom(+e.target.value / 100)}
              className="flex-1 h-1 accent-[#3d8ef0]"
            />
            <button
              onClick={() => setZoom((z) => Math.min(4, z + 0.1))}
              className="p-2 rounded-lg bg-[#161f31] hover:bg-[#1e2d47] text-[#8aaccc] transition-colors"
            >
              <ZoomIn size={15} />
            </button>
            <div className="w-px h-5 bg-white/[0.08] mx-1" />
            <button
              onClick={() => handleRotate(-1)}
              disabled={isProcessing}
              className="p-2 rounded-lg bg-[#161f31] hover:bg-[#1e2d47] text-[#8aaccc] disabled:opacity-40 transition-colors"
            >
              <RotateCcw size={15} />
            </button>
            <button
              onClick={() => handleRotate(1)}
              disabled={isProcessing}
              className="p-2 rounded-lg bg-[#161f31] hover:bg-[#1e2d47] text-[#8aaccc] disabled:opacity-40 transition-colors"
            >
              <RotateCw size={15} />
            </button>
          </div>

          <div className="flex items-center gap-3 w-full">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-[#161f31] hover:bg-[#1e2d47] text-[#8aaccc] text-sm font-medium transition-colors"
            >
              {t('avatarCropModal.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isProcessing || !croppedAreaPixels}
              className="flex-1 py-2.5 rounded-xl bg-[#3d8ef0] hover:bg-[#5ca3f5] text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <Loader size={15} className="animate-spin" /> {t('avatarCropModal.processing')}
                </>
              ) : (
                <>
                  <Check size={15} /> {t('avatarCropModal.save')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

AvatarCropModal.displayName = 'AvatarCropModal';
export default AvatarCropModal;
